import React, { FC, lazy, ReactElement, useEffect, useLayoutEffect, useRef } from 'react';

import { Route, Switch, useLocation } from 'react-router-dom';

import styles from './App.module.scss';
import { privateRoutes, publicRoutes } from './routes';

import { useUserData } from 'context';
import { ROLE_NAME } from 'enums';
import { clearTracking, getConsent, removeLegacyConsent } from 'lib/consent';
import { loadTracking } from 'lib/tracking';
import { PATHS } from 'types';
import { ProtectedRoute, PublicRoute, GlobalRoute } from 'views/components';
import { CookieBanner } from 'views/components/CookieBanner';
import { RequestErrorNotice } from 'views/components/RequestErrorNotice/RequestErrorNotice';
import { ErrorNotFound } from 'views/pages/ErrorNotFound';

const ResetPassword = lazy(() =>
  import('views/pages/auth/ResetPassword/ResetPassword').then(module => ({
    default: module.ResetPassword,
  })),
);
const ProfileSettings = lazy(() =>
  import('views/pages/ProfileSettings/ProfileSettings').then(module => ({
    default: module.ProfileSettings,
  })),
);
const UserProfile = lazy(() =>
  import('views/pages/UserProfile/UserProfile').then(module => ({
    default: module.UserProfile,
  })),
);

const App: FC = (): ReactElement => {
  const { isAppInitialized, user, clearForbiddenAccess } = useUserData();
  const location = useLocation();
  const previousLocationKey = useRef(location.key);

  // App survives the keyed Route remounts, so denial belongs to one visit only.
  useLayoutEffect(() => {
    if (previousLocationKey.current !== location.key) {
      previousLocationKey.current = location.key;
      clearForbiddenAccess();
    }
  }, [clearForbiddenAccess, location.key]);

  useEffect(() => {
    removeLegacyConsent();
    // Basic consent mode: Google and Hotjar only ever load after "granted".
    if (getConsent() === 'granted') loadTracking();
    else clearTracking();
  }, []);

  const isValuesRoleUser = user?.role.name === ROLE_NAME.MENTEE;
  const content = isAppInitialized ? (
    <div className={styles.app}>
      <RequestErrorNotice />
      <Switch>
        {publicRoutes.map(({ component, path, exact, title }) => (
          <Route key={path} path={path} exact={exact}>
            <PublicRoute component={component} pageTitle={title} />
          </Route>
        ))}

        {privateRoutes
          .map(route =>
            route.isBlockedRoute ? { ...route, isBlockedRoute: isValuesRoleUser } : route,
          )
          .filter(route => !route.isBlockedRoute)
          .map(({ component, path, exact, title }) => (
            <Route key={path} path={path} exact={exact}>
              <ProtectedRoute component={component} pageTitle={title} />
            </Route>
          ))}

        <Route path={PATHS.RESET_PASSWORD}>
          <GlobalRoute component={ResetPassword} pageTitle="reset-password.title" />
        </Route>

        <Route path={`${PATHS.USER_PROFILE}/:userId`}>
          <ProtectedRoute
            component={UserProfile}
            pageTitle="registration.disclaimer.privacy"
          />
        </Route>

        <Route path={PATHS.PROFILE_SETTINGS}>
          <ProtectedRoute
            component={ProfileSettings}
            pageTitle="user-menu.item.profile-settings"
          />
        </Route>

        <Route component={ErrorNotFound} />
      </Switch>
    </div>
  ) : (
    <div />
  );

  return (
    <>
      {content}
      <CookieBanner />
    </>
  );
};
export { App };
